import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import axios from 'axios';
import { Sprout, Layers, Loader2, MapPin } from 'lucide-react';
import './MarketplaceMap.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const CROP_EMOJI = [
  ['tomato', '🍅'], ['courgette', '🥒'], ['zucchini', '🥒'], ['cucumber', '🥒'],
  ['lettuce', '🥬'], ['cabbage', '🥬'], ['spinach', '🥬'], ['kale', '🥬'],
  ['pepper', '🫑'], ['capsicum', '🫑'], ['chilli', '🌶️'], ['chili', '🌶️'],
  ['carrot', '🥕'], ['pumpkin', '🎃'], ['corn', '🌽'], ['strawberr', '🍓'],
  ['basil', '🌿'], ['herb', '🌿'], ['mint', '🌿'], ['bean', '🫛'], ['pea', '🫛'],
  ['potato', '🥔'], ['kumara', '🍠'], ['broccoli', '🥦'], ['garlic', '🧄'],
  ['onion', '🧅'], ['eggplant', '🍆'], ['aubergine', '🍆'], ['apple', '🍎'],
  ['lemon', '🍋'], ['grape', '🍇'], ['flower', '🌸'], ['sunflower', '🌻'],
];

function cropEmoji(l) {
  const s = `${l.title || ''} ${l.variety || ''} ${l.category || ''}`.toLowerCase();
  for (const [k, e] of CROP_EMOJI) if (s.includes(k)) return e;
  if (l.category === 'seeds') return '🌱';
  if (l.category === 'seedlings') return '🌿';
  return '🥗';
}

const HEAT_LAYER = {
  id: 'mkt-heat-layer',
  type: 'heatmap',
  source: 'mkt-heat',
  slot: 'middle',
  maxzoom: 13,
  paint: {
    'heatmap-weight': ['interpolate', ['linear'], ['coalesce', ['get', 'weight'], 1], 0, 0, 6, 1],
    'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 12, 2.4],
    'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 20, 6, 46, 12, 80],
    'heatmap-opacity': 0.55,
    'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'],
      0, 'rgba(107,159,74,0)',
      0.25, 'rgba(180,211,48,0.5)',
      0.55, 'rgba(107,159,74,0.75)',
      0.8, 'rgba(212,175,55,0.85)',
      1, 'rgba(226,114,75,0.9)'],
  },
};

const heatGeo = (arr) => ({
  type: 'FeatureCollection',
  features: (arr || []).map((h) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [h.lng, h.lat] },
    properties: { weight: h.count || 1 },
  })),
});

const CAT_CHIPS = [['all', 'All'], ['seeds', 'Seeds'], ['seedlings', 'Seedlings'], ['produce', 'Produce']];
const TYPE_CHIPS = [['all', 'All'], ['offer', 'Offering'], ['wanted', 'Wanted']];

// Grid-based overlap clustering so nearby pins merge into one counted bubble;
// above ~zoom 13.5 every listing shows as its own pin.
function clusterListings(listings, zoom) {
  if (zoom >= 13.5) return listings.map((l) => ({ single: l }));
  const cell = 70 / Math.pow(2, zoom);
  const groups = {};
  listings.forEach((l) => {
    const key = `${Math.round(l.lng / cell)}_${Math.round(l.lat / cell)}`;
    (groups[key] = groups[key] || []).push(l);
  });
  return Object.entries(groups).map(([key, arr]) => arr.length === 1 ? { single: arr[0] } : {
    cluster: true, key, count: arr.length,
    lng: arr.reduce((s, x) => s + x.lng, 0) / arr.length,
    lat: arr.reduce((s, x) => s + x.lat, 0) / arr.length,
  });
}

export default function MarketplaceMap({ user, onSwapToast }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const readyRef = useRef(false);
  const applyRef = useRef(() => {});
  const swapRef = useRef(() => {});

  const [data, setData] = useState(null);
  const [scope, setScope] = useState('all');
  const [heatOn, setHeatOn] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/marketplace/map`, { params: { scope }, withCredentials: true });
      setData(r.data);
    } catch {
      setData((d) => d || { listings: [], heatmap: [], count: 0, center: [174.7633, -36.8485], zoom: 10.2, region: {} });
    } finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // Swap request handler kept fresh via a ref so DOM marker buttons never go stale.
  swapRef.current = async (l) => {
    try {
      const r = await axios.post(`${API}/marketplace/listings/${l.id}/swap-request`, {}, { withCredentials: true });
      setData((d) => d ? { ...d, listings: d.listings.map((x) => x.id === l.id ? { ...x, swap_requested: true } : x) } : d);
      onSwapToast?.({ kind: 'success', text: `Swap requested with ${l.display_name || 'grower'}.`, ownerId: r.data?.owner_id });
    } catch (e) {
      onSwapToast?.({ kind: 'error', text: e.response?.data?.detail || 'Could not request swap.' });
    }
  };

  // Build all pins + hover cards + heatmap for the current data (filtered + clustered).
  applyRef.current = () => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !data) return;

    markersRef.current.forEach(({ marker, popup }) => { popup.remove(); marker.remove(); });
    markersRef.current = [];

    const listings = (data.listings || []).filter((l) =>
      (typeFilter === 'all' || l.type === typeFilter) &&
      (catFilter === 'all' || (l.category || '').toLowerCase() === catFilter));

    const buildPin = (l) => {
      const el = document.createElement('div');
      el.className = 'mkt-pin' + (l.fresh ? ' fresh' : '');
      el.setAttribute('data-testid', `map-pin-${l.id}`);
      const ring = document.createElement('div'); ring.className = 'mkt-pin-ring';
      const dot = document.createElement('div'); dot.className = 'mkt-pin-dot'; dot.textContent = cropEmoji(l);
      el.append(ring, dot);

      // Hover mini-HUD card
      const card = document.createElement('div');
      card.className = 'mkt-card';
      const img = (l.photo_urls && l.photo_urls[0]) || l.photo_url;
      if (img) {
        const im = document.createElement('img'); im.src = img; im.alt = ''; im.loading = 'lazy';
        card.appendChild(im);
      }
      const body = document.createElement('div'); body.className = 'mkt-card-body';
      const badges = document.createElement('div'); badges.className = 'mkt-badges';
      const bt = document.createElement('span'); bt.className = 'mkt-type' + (l.type === 'wanted' ? ' wanted' : '');
      bt.textContent = l.type === 'wanted' ? 'Wanted' : 'Offering';
      const bc = document.createElement('span'); bc.className = 'mkt-cat'; bc.textContent = l.category || '';
      badges.append(bt, bc);
      if (l.fresh) { const bf = document.createElement('span'); bf.className = 'mkt-fresh'; bf.textContent = 'Fresh'; badges.appendChild(bf); }
      const title = document.createElement('div'); title.className = 'mkt-title'; title.textContent = l.title || 'Listing';
      const dist = l.distance_km != null ? `${l.distance_km} km away` : (l.region_label || 'Nearby');
      const meta = document.createElement('div'); meta.className = 'mkt-meta'; meta.textContent = `${l.display_name || 'Grower'} · ${dist}`;
      body.append(badges, title, meta);

      if (l.is_mine) {
        const s = document.createElement('div'); s.className = 'mkt-state mine';
        s.textContent = 'Your listing' + (l.request_count ? ` · ${l.request_count} request${l.request_count > 1 ? 's' : ''}` : '');
        body.appendChild(s);
      } else if (l.status === 'swapped') {
        const s = document.createElement('div'); s.className = 'mkt-state'; s.textContent = '✓ Swapped'; body.appendChild(s);
      } else if (l.swap_requested) {
        const s = document.createElement('div'); s.className = 'mkt-state'; s.setAttribute('data-testid', `map-requested-${l.id}`);
        s.textContent = '✓ Swap requested'; body.appendChild(s);
      } else {
        const btn = document.createElement('button'); btn.className = 'mkt-swap'; btn.type = 'button';
        btn.setAttribute('data-testid', `map-request-${l.id}`);
        btn.textContent = '⇄ Request swap';
        btn.addEventListener('click', () => swapRef.current(l));
        body.appendChild(btn);
      }
      card.appendChild(body);

      const popup = new mapboxgl.Popup({ offset: 26, closeButton: false, closeOnClick: true, className: 'mkt-popup', maxWidth: '240px' }).setDOMContent(card);
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' }).setLngLat([l.lng, l.lat]).setPopup(popup).addTo(map);

      let t;
      const open = () => { clearTimeout(t); popup.addTo(map); };
      const close = () => { t = setTimeout(() => popup.remove(), 150); };
      el.addEventListener('mouseenter', open);
      el.addEventListener('mouseleave', close);
      el.addEventListener('click', open);
      card.addEventListener('mouseenter', () => clearTimeout(t));
      card.addEventListener('mouseleave', close);
      markersRef.current.push({ marker, popup });
    };

    const buildCluster = (c) => {
      const el = document.createElement('div');
      el.className = 'mkt-cluster';
      el.setAttribute('data-testid', `map-cluster-${c.key}`);
      el.textContent = String(c.count);
      el.addEventListener('click', () => map.flyTo({ center: [c.lng, c.lat], zoom: Math.min(map.getZoom() + 2.5, 15), duration: 700, essential: true }));
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' }).setLngLat([c.lng, c.lat]).addTo(map);
      markersRef.current.push({ marker, popup: { remove: () => {} } });
    };

    clusterListings(listings, map.getZoom()).forEach((entry) => entry.cluster ? buildCluster(entry) : buildPin(entry.single));

    const src = map.getSource('mkt-heat');
    if (src) src.setData(heatGeo(data.heatmap));
  };

  // Initialise the map exactly once.
  useEffect(() => {
    if (!TOKEN || mapRef.current || !containerRef.current) return undefined;
    mapboxgl.accessToken = TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/standard',
      projection: 'mercator',
      center: [174.7633, -36.8485],
      zoom: 10.2,
      attributionControl: false,
      config: { basemap: { theme: 'monochrome', lightPreset: 'day', showPointOfInterestLabels: false, showTransitLabels: false } },
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
    mapRef.current = map;
    map.on('load', () => {
      if (!map.getSource('mkt-heat')) {
        map.addSource('mkt-heat', { type: 'geojson', data: heatGeo([]) });
        map.addLayer(HEAT_LAYER);
        map.setLayoutProperty('mkt-heat-layer', 'visibility', 'none');
      }
      readyRef.current = true;
      applyRef.current();
      map.on('zoomend', () => applyRef.current());
    });
    return () => {
      markersRef.current.forEach(({ marker, popup }) => { popup.remove(); marker.remove(); });
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
      readyRef.current = false;
    };
  }, []);

  // Re-render markers + heat when data or filters change.
  useEffect(() => { if (readyRef.current) applyRef.current(); }, [data, typeFilter, catFilter]);

  // Recentre when the target region changes.
  const cLat = data?.center?.[0];
  const cLng = data?.center?.[1];
  useEffect(() => {
    const map = mapRef.current;
    if (map && readyRef.current && cLat != null && cLng != null) {
      map.flyTo({ center: [cLat, cLng], zoom: data?.zoom || 10.5, duration: 900, essential: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cLat, cLng]);

  // Toggle heatmap layer.
  useEffect(() => {
    const map = mapRef.current;
    if (map && readyRef.current) map.setLayoutProperty('mkt-heat-layer', 'visibility', heatOn ? 'visible' : 'none');
  }, [heatOn]);

  const shownCount = (data?.listings || []).filter((l) =>
    (typeFilter === 'all' || l.type === typeFilter) &&
    (catFilter === 'all' || (l.category || '').toLowerCase() === catFilter)).length;

  if (!TOKEN) {
    return (
      <div className="mkt-map-wrap" data-testid="marketplace-map">
        <div className="mkt-map" />
        <div className="mkt-map-error"><MapPin className="w-6 h-6" /> Map unavailable — token not configured.</div>
      </div>
    );
  }

  return (
    <div className="mkt-map-wrap" data-testid="marketplace-map">
      <div ref={containerRef} className="mkt-map" role="application" aria-label="Vegetable Exchange map" />

      <div className="mkt-hud mkt-hud-tl">
        <div className="mkt-glass" style={{ borderRadius: 18 }}>
          <div className="mkt-hud-title"><Sprout className="w-4 h-4" /> Vegetable Exchange</div>
          <div className="mkt-hud-sub" data-testid="map-count">
            {loading ? 'Loading listings…' : `${shownCount} listing${shownCount === 1 ? '' : 's'}${data?.region?.label && scope === 'region' ? ' · ' + data.region.label : ''}`}
          </div>
        </div>
        <div className="mkt-glass mkt-filters" data-testid="map-filters">
          <div className="mkt-chip-row" data-testid="map-filter-type">
            {TYPE_CHIPS.map(([val, label]) => (
              <button key={val} className={`mkt-chip ${typeFilter === val ? 'active' : ''}`} onClick={() => setTypeFilter(val)} data-testid={`map-type-${val}`}>{label}</button>
            ))}
          </div>
          <div className="mkt-chip-row" data-testid="map-filter-category">
            {CAT_CHIPS.map(([val, label]) => (
              <button key={val} className={`mkt-chip ${catFilter === val ? 'active' : ''}`} onClick={() => setCatFilter(val)} data-testid={`map-cat-${val}`}>{label}</button>
            ))}
          </div>
        </div>
        {heatOn && (
          <div className="mkt-glass mkt-legend" style={{ borderRadius: 999 }} data-testid="map-heat-legend">
            <span>Low</span><i /><span>High surplus</span>
          </div>
        )}
      </div>

      <div className="mkt-hud mkt-hud-tr">
        <div className="mkt-glass mkt-seg" data-testid="map-scope-toggle">
          <button className={scope === 'region' ? 'active' : ''} onClick={() => setScope('region')} data-testid="map-scope-region"><MapPin className="w-3.5 h-3.5" /> My region</button>
          <button className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')} data-testid="map-scope-all">All</button>
        </div>
        <button className={`mkt-glass mkt-heat-btn ${heatOn ? 'active' : ''}`} onClick={() => setHeatOn((v) => !v)} data-testid="map-heat-toggle">
          <Layers className="w-3.5 h-3.5" /> Surplus heatmap
        </button>
      </div>

      {loading && <div className="mkt-map-loading"><Loader2 className="w-7 h-7 animate-spin" /></div>}
    </div>
  );
}
